import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { buildBookmarkListDraft, parseBookmarkListEvent } from "@/nostr/api/social"
import { KINDS, type NostrEvent } from "@/nostr/types"
import { usePublish } from "@/hooks/usePublish"
import { preferredLookupRelays } from "@/utils/url"
import { useAuth } from "./AuthContext"
import { useEventVersion, useNostr } from "./NostrContext"
import { useRelays } from "./RelayContext"

export interface BookmarksResult {
  noteIds: string[]
  event?: NostrEvent
  loading: boolean
  isBookmarked: (noteId: string) => boolean
  toggleBookmark: (noteId: string) => Promise<boolean>
}

const BookmarkContext = createContext<BookmarksResult | undefined>(undefined)

/**
 * Loads the signed-in user's kind 10003 bookmark list once and shares it with
 * every note action, instead of one subscription per note card.
 */
export function BookmarkProvider({ children }: { children: ReactNode }) {
  const { pubkey, canSign } = useAuth()
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const { publishDraft } = usePublish()
  const version = useEventVersion(store)
  const [loading, setLoading] = useState(Boolean(pubkey))

  useEffect(() => {
    if (!pubkey) {
      setLoading(false)
      return
    }
    setLoading(true)
    const subscription = pool.subscribe(
      { kinds: [KINDS.BOOKMARK_LIST], authors: [pubkey], limit: 1 },
      {
        relays: preferredLookupRelays(appRelays, 3),
        onEvent: (event) => store.add(event),
        onEose: () => setLoading(false),
      },
    )
    return () => subscription.close()
  }, [pubkey, pool, store, appRelays])

  const event = pubkey ? store.getReplaceable(KINDS.BOOKMARK_LIST, pubkey) : undefined
  const state = useMemo(
    () => parseBookmarkListEvent(event),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event, version],
  )

  const isBookmarked = useCallback(
    (noteId: string) => state.noteIds.includes(noteId),
    [state.noteIds],
  )

  const toggleBookmark = useCallback(
    async (noteId: string): Promise<boolean> => {
      if (!canSign) return false
      const wasBookmarked = state.noteIds.includes(noteId)
      const noteIds = wasBookmarked
        ? state.noteIds.filter((id) => id !== noteId)
        : [noteId, ...state.noteIds]
      const outcome = await publishDraft(
        buildBookmarkListDraft({ noteIds, addresses: state.addresses }, event?.content ?? ""),
        { successMessage: wasBookmarked ? "Removed bookmark" : "Bookmarked" },
      )
      return outcome.ok
    },
    [canSign, state, event, publishDraft],
  )

  const value = useMemo<BookmarksResult>(
    () => ({ noteIds: state.noteIds, event, loading, isBookmarked, toggleBookmark }),
    [state.noteIds, event, loading, isBookmarked, toggleBookmark],
  )

  return <BookmarkContext.Provider value={value}>{children}</BookmarkContext.Provider>
}

export function useBookmarks(): BookmarksResult {
  const context = useContext(BookmarkContext)
  if (!context) {
    // Bookmark actions degrade gracefully outside the provider (e.g. tests).
    return {
      noteIds: [],
      loading: false,
      isBookmarked: () => false,
      toggleBookmark: async () => false,
    }
  }
  return context
}
